import React, { useEffect, useMemo, useState } from "react";
import { auth, db, storage } from "../firebase";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import {
  FiCalendar,
  FiCopy,
  FiDollarSign,
  FiUploadCloud,
  FiUsers,
} from "react-icons/fi";

const UI_CURRENCY = "LKR";
const ELIGIBLE_PAYOUT_SOURCES = new Set([
  "course_sale_affiliate",
  "product_sale_affiliate",
  "service_sale_affiliate",
]);

function money(value) {
  return `${UI_CURRENCY} ${Number(value || 0).toLocaleString("en-LK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function shortId(value = "") {
  return value ? `${value.slice(0, 6)}...${value.slice(-4)}` : "Unknown";
}

function formatDate(value) {
  const date = value?.toDate?.() || value;
  const resolved = date instanceof Date ? date : date ? new Date(date) : null;
  return resolved ? resolved.toLocaleString() : "";
}

function monthKeyFromValue(value) {
  const source = value?.toDate?.() || value || new Date();
  const year = source.getFullYear();
  const month = String(source.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

function monthLabel(monthKey = "") {
  const [year, month] = String(monthKey || "").split("-").map(Number);
  if (!year || !month) return monthKey || "Unknown month";
  return new Date(year, month - 1, 1).toLocaleString("en-LK", {
    month: "long",
    year: "numeric",
  });
}

function settlementIdOf(instructorUid, affiliateUid, monthKey) {
  return `${instructorUid}_${affiliateUid}_${monthKey}`;
}

function titleOf(order = {}) {
  return (
    order.itemTitle ||
    order.courseTitle ||
    order.product?.title ||
    order.serviceTitle ||
    "Untitled item"
  );
}

function buyerLabel(order = {}) {
  return (
    order.affiliateLabel ||
    order.buyer?.name ||
    order.buyerName ||
    order.buyer?.email ||
    order.buyerEmail ||
    order.buyer?.phone ||
    order.buyerPhone ||
    order.userId ||
    "Affiliate"
  );
}

function slipKindOf(url = "") {
  return String(url || "").toLowerCase().includes(".pdf") ? "pdf" : "image";
}

function isAffiliatePayout(row = {}) {
  const affiliateUid = row.userId || null;
  const commission = Number(row.amount || 0);
  return (
    Boolean(affiliateUid) &&
    commission > 0 &&
    ELIGIBLE_PAYOUT_SOURCES.has(String(row.sourceType || ""))
  );
}

function statusTone(status = "") {
  const value = String(status || "").toLowerCase();
  if (value === "confirmed") return "bg-green-100 text-green-700";
  if (value === "disputed") return "bg-rose-100 text-rose-700";
  if (value === "submitted") return "bg-amber-100 text-amber-800";
  return "bg-blue-100 text-blue-700";
}


async function notifyAffiliate(affiliateUid, payload) {
  if (!affiliateUid) return;
  await addDoc(collection(db, "referral_users", affiliateUid, "notifications"), {
    userId: affiliateUid,
    recipientUid: affiliateUid,
    read: false,
    ...payload,
    createdAt: serverTimestamp(),
  });
}

function Modal({ open, onClose, children, max = "max-w-3xl" }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className={`w-full ${max} rounded-3xl border bg-white shadow-2xl overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export default function AffiliateSettlements() {
  const uid = auth.currentUser?.uid || "";
  const [payouts, setPayouts] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [storeInfo, setStoreInfo] = useState(null);
  const [submitState, setSubmitState] = useState({
    open: false,
    row: null,
    note: "",
    file: null,
    busy: false,
  });
  const [slipViewer, setSlipViewer] = useState({
    open: false,
    url: "",
    kind: "image",
    title: "",
  });
  const [ordersViewer, setOrdersViewer] = useState({
    open: false,
    row: null,
  });

  useEffect(() => {
    if (!uid) return;
    getDoc(doc(db, "stores", uid)).then((snap) => {
      setStoreInfo(snap.exists() ? snap.data() : null);
    });
  }, [uid]);

  useEffect(() => {
    if (!uid) return;

    const unsub = onSnapshot(
      query(collection(db, "payouts"), where("instructorUid", "==", uid)),
      (snap) => {
        setPayouts(
          snap.docs
            .map((entry) => ({ id: entry.id, ...entry.data() }))
            .filter((entry) => isAffiliatePayout(entry)),
        );
      },
      (error) => console.error("affiliate payouts stream failed", error),
    );

    return () => unsub();
  }, [uid]);

  useEffect(() => {
    if (!uid) return;
    const unsub = onSnapshot(
      query(collection(db, "affiliate_settlements"), where("instructorUid", "==", uid)),
      (snap) => {
        setSettlements(snap.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
      },
    );
    return () => unsub();
  }, [uid]);

  const rows = useMemo(() => {
    const grouped = new Map();

    for (const settlement of settlements) {
      const key = `${settlement.monthKey}__${settlement.affiliateUid}`;
      grouped.set(key, {
        id: settlement.id,
        monthKey: settlement.monthKey,
        affiliateUid: settlement.affiliateUid,
        affiliateLabel:
          settlement.affiliateSnapshot?.name ||
          settlement.affiliateSnapshot?.code ||
          shortId(settlement.affiliateUid),
        referralCode: settlement.affiliateSnapshot?.code || "",
        totalCommission: Number(settlement.totalCommission || 0),
        orderCount: Number(settlement.orderCount || 0),
        orderIds: Array.isArray(settlement.orderIds) ? settlement.orderIds : [],
        orders: [],
        status: settlement.status || "ready",
        settlement,
        lastUpdated:
          settlement.updatedAt ||
          settlement.submittedAt ||
          settlement.createdAt ||
          null,
      });
    }

    for (const payout of payouts) {
      if (!isAffiliatePayout(payout)) continue;
      const affiliateUid = payout.userId;
      const monthKey = payout.monthKey || monthKeyFromValue(payout.createdAt);
      const key = `${monthKey}__${affiliateUid}`;
      const existing = grouped.get(key) || {
        id: settlementIdOf(uid, affiliateUid, monthKey),
        monthKey,
        affiliateUid,
        affiliateLabel:
          payout.affiliateLabel ||
          payout.affiliateName ||
          payout.affiliateReferralCode ||
          shortId(affiliateUid),
        referralCode: payout.affiliateReferralCode || "",
        totalCommission: 0,
        orderCount: 0,
        orderIds: [],
        orders: [],
        status: "ready",
        settlement: null,
        payoutIds: [],
        lastUpdated: payout.updatedAt || payout.createdAt || null,
      };

      if (!existing.payoutIds.includes(payout.id)) {
        existing.payoutIds.push(payout.id);
        existing.totalCommission += Number(payout.amount || 0);
      }

      if (payout.orderId && !existing.orderIds.includes(payout.orderId)) {
        existing.orderIds.push(payout.orderId);
        existing.orderCount += 1;
      }

      existing.orders.push({
        id: payout.id,
        orderId: payout.orderId || "",
        itemTitle: payout.itemTitle || "",
        amount: Number(payout.amount || 0),
        level: payout.level || 0,
        createdAt: payout.createdAt || null,
        affiliateLabel:
          payout.affiliateLabel ||
          payout.affiliateName ||
          payout.userId ||
          "Affiliate",
        userId: payout.userId || "",
      });
      existing.referralCode =
        existing.referralCode || payout.affiliateReferralCode || "";
      grouped.set(key, existing);
    }

    return Array.from(grouped.values())
      .map((row) => ({
        ...row,
        totalCommission: Math.round(Number(row.totalCommission || 0) * 100) / 100,
      }))
      .filter((row) => row.orderCount > 0 || row.settlement)
      .sort((a, b) => {
        if (a.monthKey !== b.monthKey) return b.monthKey.localeCompare(a.monthKey);
        return Number(b.totalCommission || 0) - Number(a.totalCommission || 0);
      });
  }, [payouts, settlements, uid]);

  const stats = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.total += Number(row.totalCommission || 0);
        acc.count += 1;
        if ((row.status || "ready") === "submitted") acc.submitted += 1;
        if ((row.status || "ready") === "confirmed") acc.confirmed += 1;
        if ((row.status || "ready") === "disputed") acc.disputed += 1;
        if ((row.status || "ready") === "ready") acc.ready += 1;
        return acc;
      },
      { total: 0, count: 0, submitted: 0, confirmed: 0, disputed: 0, ready: 0 },
    );
  }, [rows]);

  async function submitSettlement() {
    const row = submitState.row;
    if (!row || !uid || submitState.busy) return;

    const existing = row.settlement || null;
    if (!submitState.file && !existing?.slipUrl) {
      alert("Please upload the payment slip before submitting.");
      return;
    }

    setSubmitState((prev) => ({ ...prev, busy: true }));
    try {
      let slipUrl = existing?.slipUrl || "";
      let slipType = existing?.slipType || "";

      if (submitState.file) {
        const safe = submitState.file.name.replace(/\s+/g, "_");
        const path = `uploads/affiliate_settlements/${uid}/${row.monthKey}/${row.affiliateUid}/${Date.now()}_${safe}`;
        const uploadRef = ref(storage, path);
        await uploadBytes(uploadRef, submitState.file, {
          contentType: submitState.file.type,
        });
        slipUrl = await getDownloadURL(uploadRef);
        slipType = submitState.file.type.startsWith("image/") ? "image" : "pdf";
      }

      const settlementId = settlementIdOf(uid, row.affiliateUid, row.monthKey);
      const batch = writeBatch(db);
      const settlementRef = doc(db, "affiliate_settlements", settlementId);
      batch.set(
        settlementRef,
        {
          instructorUid: uid,
          affiliateUid: row.affiliateUid,
          monthKey: row.monthKey,
          status: "submitted",
          totalCommission: Math.round(Number(row.totalCommission || 0) * 100) / 100,
          orderCount: row.orderIds.length,
          orderIds: row.orderIds,
          slipUrl,
          slipType,
          notes: submitState.note.trim() || null,
          instructorSnapshot: {
            uid,
            name: storeInfo?.displayName || storeInfo?.slug || "Instructor",
            slug: storeInfo?.slug || "",
          },
          affiliateSnapshot: {
            uid: row.affiliateUid,
            code: row.referralCode || existing?.affiliateSnapshot?.code || "",
            name:
              row.affiliateLabel ||
              existing?.affiliateSnapshot?.name ||
              "",
          },
          submittedAt: serverTimestamp(),
          createdAt: existing?.createdAt || serverTimestamp(),
          updatedAt: serverTimestamp(),
          affiliateReviewedAt: null,
          affiliateReviewedBy: null,
          affiliateNote: null,
        },
        { merge: true },
      );

      (row.payoutIds || []).forEach((payoutId) => {
        batch.update(doc(db, "payouts", payoutId), {
          settlementId,
          settlementStatus: "submitted",
          updatedAt: serverTimestamp(),
        });
      });

      await batch.commit();
      await notifyAffiliate(row.affiliateUid, {
        status: "submitted",
        type: "affiliate_settlement",
        settlementId,
        message: `Payout slip submitted for ${monthLabel(row.monthKey)} by ${
          storeInfo?.displayName || storeInfo?.slug || "your instructor"
        }.`,
      });
      setSubmitState({ open: false, row: null, note: "", file: null, busy: false });
    } catch (error) {
      console.error("affiliate settlement submit failed", error);
      alert("Submit failed. Please try again.");
      setSubmitState((prev) => ({ ...prev, busy: false }));
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f7f8fa] via-[#eef3ff] to-[#f6f2ff] px-4 py-6 md:px-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="rounded-3xl border bg-white p-5 shadow-sm md:p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-black tracking-tight md:text-3xl">
                Affiliate Payouts
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-gray-600 md:text-base">
                Review each month’s affiliate dues, upload the payment slip, and
                wait for the affiliate to confirm receipt.
              </p>
            </div>
            <div className="rounded-2xl bg-blue-50 px-4 py-3 text-sm text-blue-800">
              Current store: <span className="font-semibold">{storeInfo?.displayName || storeInfo?.slug || "Instructor"}</span>
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-3xl border bg-white p-5 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Total Due
            </div>
            <div className="mt-2 text-2xl font-black">{money(stats.total)}</div>
          </div>
          <div className="rounded-3xl border bg-white p-5 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Ready To Pay
            </div>
            <div className="mt-2 text-2xl font-black">{stats.ready}</div>
          </div>
          <div className="rounded-3xl border bg-white p-5 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Submitted
            </div>
            <div className="mt-2 text-2xl font-black">{stats.submitted}</div>
          </div>
          <div className="rounded-3xl border bg-white p-5 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Confirmed
            </div>
            <div className="mt-2 text-2xl font-black">{stats.confirmed}</div>
          </div>
        </div>

        <div className="space-y-4">
          {rows.length === 0 ? (
            <div className="rounded-3xl border bg-white p-10 text-center text-gray-500 shadow-sm">
              No affiliate payouts are due yet.
            </div>
          ) : (
            rows.map((row) => {
              const existingSlip = row.settlement?.slipUrl || "";
              const canSubmit = row.status !== "confirmed";
              return (
                <div
                  key={`${row.monthKey}_${row.affiliateUid}`}
                  className="rounded-3xl border bg-white p-5 shadow-sm"
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-xl font-black">{row.affiliateLabel}</h2>
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusTone(row.status)}`}>
                          {row.status || "ready"}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <span className="inline-flex items-center gap-2">
                          <FiCalendar /> {monthLabel(row.monthKey)}
                        </span>
                        <span className="inline-flex items-center gap-2">
                          <FiDollarSign /> {money(row.totalCommission)}
                        </span>
                        <span className="inline-flex items-center gap-2">
                          <FiUsers /> {row.orderCount} sale{row.orderCount === 1 ? "" : "s"}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        Affiliate UID: {row.affiliateUid}
                        {row.referralCode ? ` • Code: ${row.referralCode}` : ""}
                      </div>
                      {row.settlement?.affiliateNote && (
                        <div className="rounded-2xl bg-rose-50 px-4 py-3 text-sm text-rose-700">
                          Affiliate note: {row.settlement.affiliateNote}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="rounded-2xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
                        onClick={() => setOrdersViewer({ open: true, row })}
                      >
                        View sales
                      </button>
                      {existingSlip && (
                        <button
                          type="button"
                          className="rounded-2xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
                          onClick={() =>
                            setSlipViewer({
                              open: true,
                              url: existingSlip,
                              kind: row.settlement?.slipType || slipKindOf(existingSlip),
                              title: `${row.affiliateLabel} slip`,
                            })
                          }
                        >
                          Open slip
                        </button>
                      )}
                      {canSubmit && (
                        <button
                          type="button"
                          className="rounded-2xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                          onClick={() =>
                            setSubmitState({
                              open: true,
                              row,
                              note: row.settlement?.notes || "",
                              file: null,
                              busy: false,
                            })
                          }
                        >
                          {row.status === "submitted" ? "Resubmit payout" : "Submit payout"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <Modal
        open={submitState.open}
        onClose={() =>
          !submitState.busy &&
          setSubmitState({ open: false, row: null, note: "", file: null, busy: false })
        }
      >
        <div className="space-y-5">
          <div>
            <h3 className="text-xl font-black">Submit affiliate payout</h3>
            <p className="mt-1 text-sm text-gray-600">
              Upload the payment proof for {submitState.row?.affiliateLabel || "this affiliate"}.
            </p>
          </div>

          <div className="rounded-2xl bg-gray-50 p-4 text-sm text-gray-700">
            <div>Month: <span className="font-semibold">{monthLabel(submitState.row?.monthKey)}</span></div>
            <div className="mt-1">Amount due: <span className="font-semibold">{money(submitState.row?.totalCommission)}</span></div>
          </div>

          <label className="block rounded-2xl border border-dashed p-4 text-sm text-gray-600">
            <div className="flex items-center gap-2 font-semibold text-gray-800">
              <FiUploadCloud /> Upload slip
            </div>
            <input
              type="file"
              accept="image/*,application/pdf"
              className="mt-3 block w-full text-sm"
              onChange={(event) =>
                setSubmitState((prev) => ({ ...prev, file: event.target.files?.[0] || null }))
              }
            />
            {submitState.file && (
              <div className="mt-2 text-xs text-gray-500">{submitState.file.name}</div>
            )}
          </label>

          <label className="block text-sm font-semibold text-gray-700">
            Note
            <textarea
              rows={4}
              className="mt-2 w-full rounded-2xl border px-4 py-3 text-sm outline-none focus:border-blue-500"
              placeholder="Add any note for the affiliate"
              value={submitState.note}
              onChange={(event) =>
                setSubmitState((prev) => ({ ...prev, note: event.target.value }))
              }
            />
          </label>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="rounded-2xl border px-4 py-2 font-semibold hover:bg-gray-50"
              onClick={() =>
                setSubmitState({ open: false, row: null, note: "", file: null, busy: false })
              }
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-2xl bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={submitSettlement}
              disabled={submitState.busy}
            >
              {submitState.busy ? "Submitting..." : "Submit payout"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={ordersViewer.open}
        onClose={() => setOrdersViewer({ open: false, row: null })}
      >
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-xl font-black">Sales in {monthLabel(ordersViewer.row?.monthKey)}</h3>
              <p className="mt-1 text-sm text-gray-600">
                {ordersViewer.row?.affiliateLabel || "Affiliate"} • {money(ordersViewer.row?.totalCommission)}
              </p>
            </div>
            <button
              type="button"
              className="rounded-2xl border px-3 py-2 text-sm font-semibold hover:bg-gray-50"
              onClick={() =>
                navigator.clipboard?.writeText(
                  String(ordersViewer.row?.payoutIds?.join(", ") || ""),
                )
              }
            >
              <FiCopy className="inline-block" /> Copy payout IDs
            </button>
          </div>
          <div className="space-y-3">
            {(ordersViewer.row?.orders || []).map((order) => (
              <div key={order.id} className="rounded-2xl border p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold">{titleOf(order)}</div>
                    <div className="mt-1 text-sm text-gray-500">Buyer: {buyerLabel(order)}</div>
                  </div>
                  <div className="text-right text-sm text-gray-600">
                    <div>{money(order.amount || 0)}</div>
                    <div>
                      Level {order.level || "-"} • {formatDate(order.createdAt)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      <Modal
        open={slipViewer.open}
        onClose={() => setSlipViewer({ open: false, url: "", kind: "image", title: "" })}
        max="max-w-5xl"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-black">{slipViewer.title || "Payment slip"}</h3>
              <p className="mt-1 text-sm text-gray-600">Review the uploaded payout proof.</p>
            </div>
            <a
              href={slipViewer.url}
              target="_blank"
              rel="noreferrer"
              className="rounded-2xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
            >
              Open in new tab
            </a>
          </div>
          <div className="overflow-hidden rounded-3xl border bg-gray-50">
            {slipViewer.kind === "pdf" ? (
              <iframe title={slipViewer.title || "Slip PDF"} src={slipViewer.url} className="h-[70vh] w-full" />
            ) : (
              <img src={slipViewer.url} alt={slipViewer.title || "Slip"} className="max-h-[70vh] w-full object-contain" />
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
